import { green } from '@mui/material/colors'
import {
  alpha,
  createTheme,
  type PaletteMode,
  type Shadows
} from '@mui/material/styles'

const neutral = {
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827'
}

const shadows = [
  'none',
  '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
  '0px 1px 2px rgba(100, 116, 139, 0.12)',
  '0px 1px 4px rgba(100, 116, 139, 0.12)',
  '0px 1px 5px rgba(100, 116, 139, 0.12)',
  '0px 1px 6px rgba(100, 116, 139, 0.12)',
  '0px 2px 6px rgba(100, 116, 139, 0.12)',
  '0px 3px 6px rgba(100, 116, 139, 0.12)',
  '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
  '0px 5px 12px rgba(100, 116, 139, 0.12)',
  '0px 5px 14px rgba(100, 116, 139, 0.12)',
  '0px 5px 15px rgba(100, 116, 139, 0.12)',
  '0px 6px 15px rgba(100, 116, 139, 0.12)',
  '0px 7px 15px rgba(100, 116, 139, 0.12)',
  '0px 8px 15px rgba(100, 116, 139, 0.12)',
  '0px 9px 15px rgba(100, 116, 139, 0.12)',
  '0px 10px 15px rgba(100, 116, 139, 0.12)',
  '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
  '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
  '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
  '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)'
] as Shadows

const getPalette = (mode: PaletteMode) => {
  const isDark = mode === 'dark'

  return {
    mode,
    neutral,
    action: {
      active: isDark ? '#CBD5E1' : '#6B7280',
      focus: isDark ? 'rgba(148, 163, 184, 0.16)' : 'rgba(55, 65, 81, 0.12)',
      hover: isDark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(55, 65, 81, 0.04)',
      selected: isDark
        ? 'rgba(148, 163, 184, 0.14)'
        : 'rgba(55, 65, 81, 0.08)',
      disabledBackground: isDark
        ? 'rgba(148, 163, 184, 0.12)'
        : 'rgba(55, 65, 81, 0.12)',
      disabled: isDark
        ? 'rgba(148, 163, 184, 0.3)'
        : 'rgba(55, 65, 81, 0.26)'
    },
    background: {
      default: isDark ? '#0D1016' : '#F9FAFC',
      paper: isDark ? '#171C24' : '#FFFFFF',
      blur: isDark ? '#171C24CC' : '#FFFFFFCC'
    },
    divider: isDark ? 'rgba(148, 163, 184, 0.16)' : '#E6E8F0',
    primary: {
      main: green[600],
      light: isDark ? '#5FC46A' : '#68b36b',
      dark: '#2e7031',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: isDark ? '#232833' : '#333f48',
      light: isDark ? '#343A47' : '#5b656c',
      dark: isDark ? '#141922' : '#232c32',
      contrastText: '#FFFFFF'
    },
    success: {
      main: green[700],
      light: '#43C6B7',
      dark: '#0E8074',
      contrastText: '#FFFFFF'
    },
    info: {
      main: isDark ? '#64B6F7' : '#0B79D0', //'#2196F3',
      light: '#64B6F7',
      dark: '#0B79D0',
      contrastText: '#FFFFFF'
    },
    warning: {
      main: isDark ? '#FFBF4C' : '#B27B16', //'#FFB020',
      light: '#FFBF4C',
      dark: '#B27B16',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#D14343',
      light: '#DA6868',
      dark: '#922E2E',
      contrastText: '#FFFFFF'
    },
    accent: { // indigo — split-complement to green
      main: isDark ? '#818CF8' : '#5B5FC7', // #436DA0
      light: '#818CF8',
      dark: '#4348A8',
      contrastText: '#FFFFFF'
    },
    highlight: { // teal — analogous to green, distinct from success
      main: isDark ? '#22D3EE' : '#0891B2',
      light: '#22D3EE',
      dark: '#0E7490',
      contrastText: '#FFFFFF'
    },
    text: {
      primary: isDark ? '#E5EDF7' : '#121828',
      secondary: isDark ? '#94A3B8' : '#65748B',
      disabled: isDark
        ? 'rgba(148, 163, 184, 0.48)'
        : 'rgba(55, 65, 81, 0.48)',
      dark: {
        primary: '#121828',
        secondary: '#65748B',
        disabled: 'rgba(55, 65, 81, 0.48)',
        },
      light: {
        primary: '#E5EDF7',
        secondary: '#94A3B8',
        disabled: 'rgba(148, 163, 184, 0.48)',
      },
    },
    sidebar: {
      drawerBg: isDark
        ? 'linear-gradient(180deg, #11151c 0%, #0d1016 100%)'
        : 'linear-gradient(180deg, #f8f9fc 0%, #f1f4f8 100%)',
      drawerBorder: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(90, 95, 110, 0.12)',
      drawerShadow: isDark
        ? '0 18px 40px rgba(2, 6, 23, 0.45)'
        : '0 10px 30px rgba(20, 24, 40, 0.16)',
      drawerShadowMini: isDark
        ? '0 10px 28px rgba(2, 6, 23, 0.35)'
        : '0 8px 24px rgba(20, 24, 40, 0.09)',
      popperBg: isDark ? 'rgba(19, 24, 33, 0.94)' : 'rgba(255,255,255,0.95)',
      itemHover: isDark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(0, 0, 0, 0.05)',
      selectedBg: alpha(green[500], isDark ? 0.24 : 0.16),
      selectedHover: alpha(green[500], isDark ? 0.3 : 0.22),
      selectedColor: isDark ? '#DCFCE7' : '#22543d',
      floatingBg: isDark ? 'rgba(17, 21, 28, 0.82)' : 'rgba(255,255,255,0.25)',
      floatingColor: isDark ? 'rgba(241, 245, 249, 0.95)' : 'rgba(0,0,0,0.8)',
      floatingBorder: isDark
        ? 'rgba(148, 163, 184, 0.22)'
        : 'rgba(255,255,255,0.4)',
      floatingHover: isDark
        ? 'rgba(27, 33, 43, 0.96)'
        : 'rgba(255,255,255,0.4)',
      mainBg: isDark
        ? 'radial-gradient(900px 520px at 100% -120px, rgba(64, 145, 108, 0.08), transparent), linear-gradient(180deg, #10141b 0%, #0b0f15 100%)'
        : 'radial-gradient(1200px 600px at 100% -80px, rgba(163, 196, 243, 0.18), transparent), linear-gradient(180deg, #f7f9fc 0%, #f1f5f9 100%)'
    }
  }
}

const createAppTheme = (mode: PaletteMode = 'light') => {
  const isDark = mode === 'dark'
  const palette = getPalette(mode)
  type PaletteToken = {
    main: string
    dark?: string
    contrastText?: string
  }
  const paletteTokens = palette as unknown as Record<string, PaletteToken | undefined>
  const getSemanticPalette = (color?: string): PaletteToken => {
    if (!color) return palette.primary
    const value = paletteTokens[color]
    return value?.main ? value : palette.primary
  }
  const getInteractivePalette = (color?: string): PaletteToken => {
    if (isDark && color === 'secondary') {
      return {
        main: '#CBD5E1',
        dark: '#E2E8F0',
        contrastText: '#0F172A'
      }
    }

    return getSemanticPalette(color)
  }

  const getButtonStyles = (
    ownerState: { color?: string; variant?: string } = {}
  ) => {
    const hasSemanticColor = Boolean(
      ownerState.color &&
        ownerState.color !== 'inherit' &&
        paletteTokens[ownerState.color]?.main
    )
    const buttonPalette = getInteractivePalette(ownerState.color)
    let baseTextColor = palette.text.primary

    if (ownerState.color === 'inherit') {
      baseTextColor = palette.text.primary
    } else if (hasSemanticColor) {
      baseTextColor = buttonPalette.main
    }
    const focusRing = `0 0 0 3px ${alpha(buttonPalette.main, isDark ? 0.3 : 0.16)}`

    return {
      textTransform: 'none',
      borderRadius: 12,
      fontWeight: 600,
      letterSpacing: 0.1,
      transition:
        'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease, transform 0.2s ease',
      '&:focus-visible': {
        boxShadow: focusRing
      },
      '&.Mui-disabled': {
        color: palette.text.disabled,
        borderColor: palette.action.disabledBackground,
        backgroundColor:
          ownerState.variant === 'contained'
            ? palette.action.disabledBackground
            : 'transparent'
      },
      ...(ownerState.variant === 'contained' && {
        backgroundColor: buttonPalette.main,
        color: buttonPalette.contrastText || '#FFFFFF',
        border: '1px solid transparent',
        boxShadow: isDark
          ? `0 10px 24px ${alpha(buttonPalette.main, 0.22)}`
          : `0 8px 18px ${alpha(buttonPalette.main, 0.16)}`,
        '&:hover': {
          backgroundColor: buttonPalette.dark || buttonPalette.main,
          boxShadow: isDark
            ? `0 12px 28px ${alpha(buttonPalette.main, 0.28)}`
            : `0 10px 20px ${alpha(buttonPalette.main, 0.2)}`
        }
      }),
      ...(ownerState.variant === 'outlined' && {
        color: baseTextColor,
        borderColor:
          ownerState.color === 'inherit'
            ? alpha(palette.text.primary, isDark ? 0.24 : 0.16)
            : alpha(buttonPalette.main, isDark ? 0.56 : 0.24),
        backgroundColor:
          ownerState.color === 'inherit'
            ? alpha(palette.text.primary, isDark ? 0.04 : 0.02)
            : alpha(buttonPalette.main, isDark ? 0.12 : 0.03),
        '&:hover': {
          borderColor:
            ownerState.color === 'inherit' ? palette.text.primary : buttonPalette.main,
          backgroundColor:
            ownerState.color === 'inherit'
              ? alpha(palette.text.primary, isDark ? 0.08 : 0.05)
              : alpha(buttonPalette.main, isDark ? 0.18 : 0.06)
        }
      }),
      ...((!ownerState.variant || ownerState.variant === 'text') && {
        color: baseTextColor,
        '&:hover': {
          backgroundColor:
            ownerState.color === 'inherit'
              ? alpha(palette.text.primary, isDark ? 0.08 : 0.05)
              : alpha(buttonPalette.main, isDark ? 0.16 : 0.08)
        }
      })
    }
  }

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1000,
        lg: 1200,
        xl: 1920
      }
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: ({ ownerState }) => getButtonStyles(ownerState),
          sizeSmall: {
            padding: '6px 16px'
          },
          sizeMedium: {
            padding: '8px 20px'
          },
          sizeLarge: {
            padding: '11px 24px'
          },
          ...{
            textSizeSmall: {
              padding: '7px 12px'
            },
            textSizeMedium: {
              padding: '9px 16px'
            },
            textSizeLarge: {
              padding: '12px 16px'
            }
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            outline: 'none',
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const hasSemanticColor = Boolean(
              ownerState.color &&
                ownerState.color !== 'inherit' &&
                paletteTokens[ownerState.color]?.main
            )
            const iconPalette = hasSemanticColor
              ? getInteractivePalette(ownerState.color)
              : { main: isDark ? '#CBD5E1' : palette.text.secondary }
            let iconColor = iconPalette.main

            if (ownerState.color === 'inherit') {
              iconColor = isDark ? '#E5EDF7' : palette.text.primary
            }

            return {
              borderRadius: 10,
              color: iconColor,
              transition:
                'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              ...(isDark && {
                backgroundColor: alpha(iconPalette.main, hasSemanticColor ? 0.12 : 0.06),
                '&:hover': {
                  backgroundColor: alpha(iconPalette.main, hasSemanticColor ? 0.18 : 0.12),
                  transform: 'translateY(-1px)'
                }
              }),
              ...(!isDark && {
                '&:hover': {
                  backgroundColor: alpha(iconPalette.main, hasSemanticColor ? 0.1 : 0.06)
                }
              }),
              '&.Mui-disabled': {
                color: palette.action.disabled,
                backgroundColor: isDark ? palette.action.disabledBackground : 'transparent'
              }
            }
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.MuiTab-labelIcon': {
              minHeight: '48px',
              height: '48px',
            },
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.2s ease',
            '&.MuiTableRow-hover:hover': {
              backgroundColor: palette.action.hover
            },
            '&.Mui-selected': {
              backgroundColor: `${alpha(palette.primary.main, isDark ? 0.12 : 0.06)} !important`
            },
            '&.Mui-selected:hover': {
              backgroundColor: `${alpha(palette.primary.main, isDark ? 0.18 : 0.1)} !important`
            }
          }
        }
      },
      MuiFab: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const fabPalette = getSemanticPalette(ownerState.color)

            return {
              borderRadius: 18,
              backgroundColor: fabPalette.main,
              color: fabPalette.contrastText || '#FFFFFF',
              boxShadow: isDark
                ? `0 14px 30px ${alpha(fabPalette.main, 0.28)}`
                : `0 12px 24px ${alpha(fabPalette.main, 0.18)}`,
              '&:hover': {
                backgroundColor: fabPalette.dark || fabPalette.main,
                boxShadow: isDark
                  ? `0 18px 34px ${alpha(fabPalette.main, 0.32)}`
                  : `0 14px 28px ${alpha(fabPalette.main, 0.22)}`
              }
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999
          },
          outlined: ({ ownerState }) => {
            const colorKey = ownerState.color
            const paletteEntry =
              colorKey && colorKey !== 'default'
                ? paletteTokens[colorKey]
                : undefined
            const chipPalette = paletteEntry?.main
              ? paletteEntry
              : { main: palette.text.secondary }

            return {
              borderColor: alpha(chipPalette.main, isDark ? 0.32 : 0.18),
              backgroundColor: isDark ? alpha(chipPalette.main, 0.08) : 'transparent',
              color: isDark ? palette.text.primary : undefined
            }
          }
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            '&:last-child': {
              paddingBottom: '24px'
            }
          }
        }
      },
      MuiCardHeader: {
        defaultProps: {
          slotProps: { 
            title: {
              variant: 'h6'
            },
            subheader: {
              variant: 'body2'
            }
          }
        },
        styleOverrides: {
          root: {
            padding: '32px 24px'
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            boxSizing: 'border-box',
            margin: 0,
            padding: 0,
            scrollbarWidth: 'thin',
            scrollbarColor: `${
              isDark ? 'rgba(148, 163, 184, 0.32)' : 'rgba(106, 125, 145, 0.45)'
            } transparent`
          },
          html: {
            MozOsxFontSmoothing: 'grayscale',
            WebkitFontSmoothing: 'antialiased',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            width: '100%',
            colorScheme: mode
          },
          body: {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            minHeight: '100%',
            width: '100%',
            backgroundColor: palette.background.default,
            color: palette.text.primary
          },
          '#root': {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            height: '100%',
            width: '100%'
          },
          '::selection': {
            backgroundColor: alpha(palette.primary.main, isDark ? 0.32 : 0.18)
          },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8
          },
          '*::-webkit-scrollbar-thumb': {
            background: isDark
              ? 'rgba(148, 163, 184, 0.32)'
              : 'rgba(106, 125, 145, 0.45)',
            borderRadius: 999
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${palette.divider}`
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backdropFilter: 'blur(8px)',
            backgroundColor: palette.background.blur,
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? alpha('#fff', 0.02) : palette.background.paper,
            color: palette.text.primary,
            transition:
              'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? alpha(neutral[200], 0.4) : alpha(neutral[700], 0.24)
            },
            '&.Mui-focused': {
              backgroundColor: isDark ? alpha('#fff', 0.03) : palette.background.paper,
              boxShadow: `0 0 0 3px ${alpha(palette.primary.main, isDark ? 0.18 : 0.1)}`
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.primary.main
            }
          },
          notchedOutline: {
            borderColor: isDark ? alpha(neutral[300], 0.18) : '#E6E8F0'
          },
          input: {
            '&::placeholder': {
              color: palette.text.secondary,
              opacity: 1
            }
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? alpha('#fff', 0.04) : '#F3F4F6',
            '.MuiTableCell-root': {
              color: isDark ? '#CBD5E1' : '#374151'
            },
            borderBottom: 'none',
            '& .MuiTableCell-root': {
              borderBottom: 'none',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: 0.5,
              textTransform: 'uppercase'
            },
            '& .MuiTableCell-paddingCheckbox': {
              paddingTop: 4,
              paddingBottom: 4
            }
          }
        }
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderTop: `1px solid ${palette.divider}`,
            backgroundColor: isDark
              ? alpha('#fff', 0.03)
              : alpha('#f5f5f5', 0.9),
            color: palette.text.secondary
          },
          toolbar: {
            minHeight: 52
          },
          select: {
            color: palette.text.primary
          },
          displayedRows: {
            color: palette.text.secondary
          },
          selectLabel: {
            color: palette.text.secondary
          },
          actions: {
            color: palette.text.secondary
          }
        }
      }
    },
    palette,
    shape: {
      borderRadius: 8
    },
    shadows,
    typography: {
      button: {
        fontWeight: 600
      },
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.57
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.75
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.57
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.5px',
        lineHeight: 2.5,
        textTransform: 'uppercase'
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.66
      },
      h1: {
        fontWeight: 700,
        fontSize: '3.5rem',
        lineHeight: 1.375
      },
      h2: {
        fontWeight: 700,
        fontSize: '3rem',
        lineHeight: 1.375
      },
      h3: {
        fontWeight: 700,
        fontSize: '2.25rem',
        lineHeight: 1.375
      },
      h4: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.375
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.375
      },
      h6: {
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 1.375
      }
    },
    constants: {
      widgetMaxHeight: {
        xs: "calc(100vh - 49px - 32px - 84px)",
        md: "calc(100vh - 49px - 32px)",
      },
      widgetOffsetTop: 49, // should be number
      miniDrawerWidth: 69,
      fullDrawerWidth: 340,
    },
  })
}

export default createAppTheme
